import { useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { effPrice } from "./PublicMenu";

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
    {children}
  </button>
);

const Group = ({ title, note, children }) => (
  <div>
    <p className="mb-1.5 text-xs font-semibold text-slate-700">{title} {note && <span className="font-normal text-slate-400">{note}</span>}</p>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

const defaults = (vgroups) => Object.fromEntries(vgroups.map((g) => {
  const opts = g.options.filter((o) => o.is_active);
  const d = opts.find((o) => o.is_default) || (g.is_required ? opts[0] : null);
  return [g.id, d?.id || ""];
}));

export function ProductSheet({ product, menu }) {
  const { addItem } = useCart();
  const vgroups = menu.variation_groups.filter((g) => product.variation_group_ids?.includes(g.id));
  const agroups = menu.addon_groups.filter((g) => product.addon_group_ids?.includes(g.id));
  const sizes = product.sizes || [];
  const [vars, setVars] = useState(() => defaults(vgroups));
  const [sizeId, setSizeId] = useState("");
  const [adds, setAdds] = useState({});
  const [qty, setQty] = useState(1);

  const unit = useMemo(() => {
    let u = effPrice(product);
    const fixed = vgroups.find((g) => g.price_mode === "fixed" && vars[g.id]);
    if (fixed) u = fixed.options.find((o) => o.id === vars[fixed.id])?.price || 0;
    if (sizes.length) u = sizes.find((s) => s.id === sizeId)?.price ?? Math.min(...sizes.map((s) => s.price));
    vgroups.filter((g) => g.price_mode !== "fixed").forEach((g) => { u += g.options.find((o) => o.id === vars[g.id])?.price || 0; });
    agroups.forEach((g) => (adds[g.id] || []).forEach((id) => { u += g.options.find((o) => o.id === id)?.price || 0; }));
    return u;
  }, [vars, adds, sizeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAdd = (g, id) => {
    const cur = adds[g.id] || [];
    if (cur.includes(id)) return setAdds({ ...adds, [g.id]: cur.filter((x) => x !== id) });
    if (g.max_select && cur.length >= g.max_select) return toast.error(`Choose up to ${g.max_select} for ${g.name}`);
    setAdds({ ...adds, [g.id]: [...cur, id] });
  };

  const add = () => {
    if (sizes.length && !sizeId) return toast.error("Please choose a size");
    for (const g of vgroups) if (g.is_required && !vars[g.id]) return toast.error(`Please choose ${g.name}`);
    for (const g of agroups) {
      const n = (adds[g.id] || []).length;
      if (n < (g.min_select || 0)) return toast.error(`Choose at least ${g.min_select} for ${g.name}`);
    }
    const labels = [
      ...(sizeId ? [sizes.find((s) => s.id === sizeId)?.name] : []),
      ...vgroups.filter((g) => vars[g.id]).map((g) => g.options.find((o) => o.id === vars[g.id])?.name),
      ...agroups.flatMap((g) => (adds[g.id] || []).map((id) => g.options.find((o) => o.id === id)?.name)),
    ];
    addItem({
      product_id: product.id, name: product.name, quantity: qty, unit_price: unit, labels, size_id: sizeId,
      variation_selections: vgroups.filter((g) => vars[g.id]).map((g) => ({ group_id: g.id, option_id: vars[g.id] })),
      addon_selections: agroups.filter((g) => (adds[g.id] || []).length).map((g) => ({ group_id: g.id, option_ids: adds[g.id] })),
    });
    toast.success(`${product.name} added`);
    setVars(defaults(vgroups)); setSizeId(""); setAdds({}); setQty(1);
  };

  return (
    <div className="mt-3 space-y-2.5" data-testid={`item-options-${product.id}`}>
      {sizes.length > 0 && (
        <Group title="Size" note="Required">
          {sizes.map((s) => <Chip key={s.id} on={sizeId === s.id} onClick={() => setSizeId(s.id)} testId={`size-option-${s.id}`}>{s.name} · {money(s.price)}</Chip>)}
        </Group>
      )}
      {vgroups.map((g) => (
        <Group key={g.id} title={g.name} note={g.is_required ? "Required" : "Optional"}>
          {g.options.filter((o) => o.is_active).map((o) => (
            <Chip key={o.id} on={vars[g.id] === o.id} testId={`variation-option-${o.id}`}
              onClick={() => setVars({ ...vars, [g.id]: !g.is_required && vars[g.id] === o.id ? "" : o.id })}>
              {o.name}{g.price_mode === "fixed" ? ` · ${money(o.price)}` : o.price > 0 ? ` +${money(o.price)}` : ""}
            </Chip>
          ))}
        </Group>
      ))}
      {agroups.map((g) => (
        <Group key={g.id} title={g.name} note={`${g.min_select ? `Min ${g.min_select}` : "Optional"}${g.max_select ? ` · Max ${g.max_select}` : ""}`}>
          {g.options.filter((o) => o.is_active).map((o) => (
            <Chip key={o.id} on={(adds[g.id] || []).includes(o.id)} onClick={() => toggleAdd(g, o.id)} testId={`addon-option-${o.id}`}>
              {o.name}{o.price > 0 && ` +${money(o.price)}`}
            </Chip>
          ))}
        </Group>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <div className="flex items-center rounded-full border border-slate-200">
          <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-1.5" data-testid={`qty-minus-${product.id}`}><Minus className="h-3.5 w-3.5" /></button>
          <span className="w-5 text-center text-sm font-semibold" data-testid={`qty-${product.id}`}>{qty}</span>
          <button type="button" onClick={() => setQty(Math.min(50, qty + 1))} className="p-1.5" data-testid={`qty-plus-${product.id}`}><Plus className="h-3.5 w-3.5" /></button>
        </div>
        <Button size="sm" onClick={add} className="h-8 rounded-full bg-emerald-500 px-4 hover:bg-emerald-600" data-testid={`add-to-cart-${product.id}`}>
          Add to cart · {money(unit * qty)}
        </Button>
      </div>
    </div>
  );
}
